import React from 'react';
import {ToasterWithStatusAndDesriptionParams} from '../../context-providers/ToasterProvider/ToasterProvider';
import {contractAddresses} from '../../helpers/config/contracts.config';
import {getErrorMessage} from '../../helpers/error-message.helper';
import Singleton from '../../helpers/singleton';
import {BigNumber, constants} from 'ethers';
import {earnBalanceStore} from '../../stores/EarnBalanceStore';

interface InjectedDependencies {
    publicClient: any;
    nrsContract: any;
    lpNrsUsdcContract: any,
    lpNrsUsdcStakingContract: any,
    lpNxusdUsdcContract: any,
    lpNxusdUsdcStakingContract: any,
    nrsWithdrawalTimelockContract: any,
    userAddress: `0x${string}` | undefined;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    removeToaster: (id: string) => void;
    closeModal: () => void;
    signTypedData: (data: any) => Promise<string>;
}

@Singleton
export class DepositWithdrawalEarnService {

    private publicClient!: any;
    private nrsContract!: any;
    private lpNrsUsdcContract!: any;
    private lpNrsUsdcStakingContract!: any;
    private lpNxusdUsdcContract!: any;
    private lpNxusdUsdcStakingContract!: any;
    private userAddress?: string;
    private MAX_AMOUNT = constants.MaxUint256;
    private showToaster!: (params: ToasterWithStatusAndDesriptionParams) => void;
    private removeToaster!: (id: string) => void;
    private closeModal!: () => void;
    private signTypedData!: (data: any) => Promise<string>;

    public injectDependencies(deps: InjectedDependencies) {
        this.publicClient = deps.publicClient;
        this.nrsContract = deps.nrsContract;
        this.userAddress = deps.userAddress;
        this.lpNrsUsdcContract = deps.lpNrsUsdcContract;
        this.lpNrsUsdcStakingContract = deps.lpNrsUsdcStakingContract;
        this.lpNxusdUsdcContract = deps.lpNxusdUsdcContract;
        this.lpNxusdUsdcStakingContract = deps.lpNxusdUsdcStakingContract;
        this.showToaster = deps.addToasterWithStatusAndDescription;
        this.removeToaster = deps.removeToaster;
        this.closeModal = deps.closeModal;
        this.signTypedData = deps.signTypedData;
    }

    public async withdrawNRS_USDC_LP(amountBN: BigNumber) {
        const toasterId = this.getToasterId('withdraw', amountBN.toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        const withdrawPendingDescription = this.getWithdrawToasterDescription();

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Withdraw NRS-USDC LP',
            description: withdrawPendingDescription
        });
        try {
            const claimRewards = false
            const hash = await this.lpNrsUsdcStakingContract.write.withdraw([amountBN, claimRewards]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNrsUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Withdraw NRS-USDC LP',
                description: withdrawPendingDescription,
                closeTime: 10000,
            })
        } catch (err: any) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Withdraw NRS-USDC LP',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async withdrawNXUSD_USDC_LP(amountBN: BigNumber) {
        const toasterId = this.getToasterId('withdraw', amountBN.toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        const withdrawPendingDescription = this.getWithdrawToasterDescription();

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Withdraw NXUSD-USDC LP',
            description: withdrawPendingDescription
        });
        try {
            const claimRewards = false
            const hash = await this.lpNxusdUsdcStakingContract.write.withdraw([amountBN, claimRewards]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNxusdUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Withdraw NXUSD-USDC LP',
                description: withdrawPendingDescription,
                closeTime: 10000,
            })
        } catch (err: any) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Withdraw NXUSD-USDC LP',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async claimFromNRS_USDC_LP_Staking() {
        const toasterId = this.getToasterId('claim', Date.now().toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Claim NRS',
            description: ''
        });
        try {
            const hash = await this.lpNrsUsdcStakingContract.write.claim();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNrsUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Claim NRS',
                description: '',
                closeTime: 10000,
            })
        } catch (err: any) {
            console.log('err', err)
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Claim NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async claimFromNXUSD_USDC_LP_Staking() {
        const toasterId = this.getToasterId('claim', Date.now().toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Claim NRS',
            description: ''
        });
        try {
            const hash = await this.lpNxusdUsdcStakingContract.write.claim();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNxusdUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Claim NRS',
                description: '',
                closeTime: 10000,
            })
        } catch (err: any) {
            console.log('err', err)
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Claim NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async approveAmountNRS_USDC_LP(amount: BigNumber) {
        const toasterId = this.getToasterId('approve', amount.toString());
        const approveDescription = 'Approve Nereus to use NRS-USDC LP for making deposits.';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Approve NRS-USDC LP',
            description: approveDescription
        });
        try {
            const hash = await this.lpNrsUsdcContract.write.approve([contractAddresses.base.lpNrsUsdcStakingContract, amount]);
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Approve NRS-USDC LP',
                description: approveDescription,
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Approve NRS-USDC LP',
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    public async approveAmountNXUSD_USDC_LP(amount: BigNumber) {
        const toasterId = this.getToasterId('approve', amount.toString());
        const approveDescription = 'Approve Nereus to use NXUSD-USDC LP for making deposits.';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Approve NXUSD-USDC LP',
            description: approveDescription
        });
        try {
            const hash = await this.lpNxusdUsdcContract.write.approve([contractAddresses.base.lpNxusdUsdcStakingContract, amount]);
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Approve NXUSD-USDC LP',
                description: approveDescription,
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Approve NXUSD-USDC LP',
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    public async depositNRS_USDC_LP(amountBN: BigNumber) {
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }
        const toasterId = this.getToasterId('deposit', amountBN.toString());
        const title = 'Staking NRS-USDC LP';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title,
            description: this.getDepositToasterDescription()
        });
        try {
            const hash = await this.lpNrsUsdcStakingContract.write.deposit([amountBN]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNrsUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Stake NRS-USDC LP',
                description: this.getDepositToasterDescription(),
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title,
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }


    public async depositNXUSD_USDC_LP(amountBN: BigNumber) {
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }
        const toasterId = this.getToasterId('deposit', amountBN.toString());
        const title = 'Staking NXUSD-USDC LP';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title,
            description: this.getDepositToasterDescription()
        });
        try {
            const hash = await this.lpNxusdUsdcStakingContract.write.deposit([amountBN]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            earnBalanceStore.invalidateNxusdUsdcAprCache();
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Stake NXUSD-USDC LP',
                description: this.getDepositToasterDescription(),
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title,
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    private getToasterId(actionType: string, amount: string) {
        return JSON.stringify({
            actionType,
            amount,
            timestamp: Date.now()
        });
    }

    public getWithdrawToasterDescription() {
        return <></>
    }

    public getDepositToasterDescription() {
        return <>Your funds will be available on your balance shortly.</>
    }
}